import React, { useContext, useEffect, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { ButtonProps, Theme } from "@literati/public-ui-shared-react";
import { CheckoutContext } from "contexts/CheckoutProvider";
import { AccountContext } from "contexts/AccountProvider";
import { submitFormik } from "utils/forms";
import { CreateDummyAccountResponseCamelCase } from "lib/api/account";
import { defer } from "lodash";
import { facebookPixelTrackCompleteRegistration } from "utils/facebook/facebookPixelTrackEvent";
import { eventEmailCapture } from "analytics/ga4";
import { useRouter } from "next/router";
import AuthFormSections, {
  SubmitStateChangeEventHandler,
} from "components/authenticate/AuthFormSections";
import {
  AuthKey as Key,
  AuthSubmit,
  AuthValues,
  AuthValuesOptionals,
  FormAction,
  SocialLoginSuccess,
  FormActionDummyAccount,
  FormActionFullAccount,
  Flow,
} from "components/authenticate/authTypes";

export const FORM_ID = "auth-form";

const emailValidator = Yup.string()
  .email("Enter a valid email.")
  .required("Required.");

export const VALIDATION_SCHEMA_DUMMY_ACCOUNT = Yup.object().shape({
  [Key.EMAIL]: emailValidator,
  [Key.TERMS]: Yup.boolean()
    .oneOf([true])
    .required("Please agree with the terms to continue."),
});
export const VALIDATION_SCHEMA_ACCOUNT = Yup.object().shape({
  [Key.LOGIN]: emailValidator,
  [Key.PASSWORD]: Yup.string()
    .min(12, "Password is too short - should be at least 12 characters.")
    .matches(/\D/, "Must not be entirely numeric")
    .required("Required."),
});
export const VALIDATION_SCHEMA_ACCOUNT_WITH_TERMS = Yup.object().shape({
  [Key.LOGIN]: emailValidator,
  [Key.TERMS]: Yup.boolean()
    .oneOf([true])
    .required("Please agree with the terms to continue."),
  [Key.PASSWORD]: Yup.string()
    .min(12, "Password is too short - should be at least 12 characters.")
    .matches(/\D/, "Must not be entirely numeric")
    .required("Required."),
});
export const VALIDATION_SCHEMA_LOGIN = Yup.object().shape({
  [Key.LOGIN]: Yup.string().required("Required"),
  [Key.PASSWORD]: Yup.string().required("Required."),
});

const VALIDATION_SCHEMA: Record<
  FormAction,
  | typeof VALIDATION_SCHEMA_DUMMY_ACCOUNT
  | typeof VALIDATION_SCHEMA_ACCOUNT
  | typeof VALIDATION_SCHEMA_LOGIN
> = {
  createDummyAccount: VALIDATION_SCHEMA_DUMMY_ACCOUNT,
  createAccount: VALIDATION_SCHEMA_ACCOUNT,
  login: VALIDATION_SCHEMA_LOGIN,
};
const getValidationSchema = ({
  flow,
  formAction,
  withTerms,
}: {
  flow: Flow;
  formAction: FormAction;
  withTerms?: boolean;
}) => {
  const validationSchema =
    flow === "full-account" && formAction === "createAccount" && !!withTerms
      ? VALIDATION_SCHEMA_ACCOUNT_WITH_TERMS
      : VALIDATION_SCHEMA[formAction];
  return validationSchema;
};

export const INITIAL_VALUES_DEFAULT: Omit<AuthValues, Key.MAILING_LIST> = {
  [Key.KIND]: "auth",
  [Key.EMAIL]: "",
  [Key.LOGIN]: "",
  [Key.PASSWORD]: "",
  [Key.TERMS]: false,
} as const;
export const getInitialValues = ({
  formAction,
  withTerms,
}: {
  formAction: FormAction;
  withTerms?: boolean;
}): AuthValues => {
  const checked =
    (formAction === "createAccount" && withTerms) ||
    formAction === "createDummyAccount";
  return { ...INITIAL_VALUES_DEFAULT, [Key.MAILING_LIST]: checked };
};

const CLASS_BASE = "auth-form";
export const AuthFormClassName = {
  BASE: CLASS_BASE,
} as const;

type AuthFormSharedProps = {
  className?: string;
  onSocialLoginSuccess?: SocialLoginSuccess;
  onSubmit?: AuthSubmit;
  onToggleFormAction?: (formAction: FormAction) => void;
  showSocialLogin?: boolean;
  submitButtonProps?: ButtonProps;
};

export type AuthFormProps =
  | (AuthFormSharedProps & {
      flow: "dummy-account";
      initialFormAction: FormActionDummyAccount;
      initialValues?: AuthValuesOptionals;
      onSuccess?: (
        res?: CreateDummyAccountResponseCamelCase,
        phoneNumber?: string
      ) => void;
      onSubmitStateChange?: SubmitStateChangeEventHandler;
      withTerms?: true;
    })
  | (AuthFormSharedProps & {
      flow: "full-account";
      initialFormAction: FormActionFullAccount;
      initialValues?: AuthValuesOptionals;
      onSuccess?: () => void;
      onSubmitStateChange?: SubmitStateChangeEventHandler;
      withTerms: boolean;
    });

export const AuthForm = ({
  className = "",
  flow,
  initialFormAction,
  initialValues,
  onSocialLoginSuccess,
  onSubmit,
  onSuccess,
  onToggleFormAction,
  onSubmitStateChange,
  showSocialLogin = false,
  withTerms,
  submitButtonProps,
}: AuthFormProps) => {
  const cls = AuthFormClassName;

  const router = useRouter();
  const {
    session: checkoutSession,
    createDummyAccount,
    getCheckoutSession,
  } = useContext(CheckoutContext);
  const { createAccount, login } = useContext(AccountContext);

  const [formAction, setFormAction] = useState<FormAction>(initialFormAction);
  const handleFormToggleClick = () => {
    setFormAction((prev) =>
      prev === "createAccount" || prev === "createDummyAccount"
        ? "login"
        : flow === "dummy-account"
        ? "createDummyAccount"
        : "createAccount"
    );
  };

  const [phoneNumber, setPhoneNumber] = useState("");
  const handlePhoneNumberChange = (phoneNumber: string) => {
    setPhoneNumber(phoneNumber);
  };

  useEffect(() => {
    onToggleFormAction?.(formAction);
  }, [formAction, onToggleFormAction]);

  const [INITIAL_VALUES] = useState<AuthValues>({
    ...getInitialValues({ formAction, withTerms }),
    ...initialValues,
  });

  const handleSubmit: AuthSubmit = async (values, formikProps) => {
    switch (formAction) {
      case "createDummyAccount":
        return await submitFormik({
          onSubmit: async () => {
            const res = (await createDummyAccount?.({
              ...values,
              safeForSignup: true,
            })) as CreateDummyAccountResponseCamelCase;
            const userId = res?.uid;
            const url = router?.route;
            if (url && userId) {
              defer(() =>
                facebookPixelTrackCompleteRegistration({ url, userId })
              );
            }
            onSubmit?.(values, formikProps);
            return res;
          },
          onSuccess: (res: CreateDummyAccountResponseCamelCase) => {
            window.dataLayer?.push({ event: "kids_email_capture" });
            // GA4 Data Layer implementation uses gtag. This should live next to UA until UA is retired.
            eventEmailCapture();
            getCheckoutSession?.();
            onSuccess?.(res, phoneNumber);
          },
          formikProps,
        });
      case "createAccount":
        return await submitFormik({
          onSubmit: async () => {
            const { login, mailingList: formMailingList, password } = values;

            const mailingList = withTerms
              ? formMailingList
              : Boolean(checkoutSession?.accountInfo?.mailingList);

            const res = await createAccount({
              email: login,
              password,
              mailingList,
            });
            onSubmit?.(values, formikProps);
            return res;
          },
          onSuccess: async () => {
            getCheckoutSession?.();
            onSuccess?.(undefined, phoneNumber);
          },
          formikProps,
        });
      case "login":
        return await submitFormik({
          onSubmit: async () => {
            const res = await login(values);
            onSubmit?.(values, formikProps);
            return res;
          },
          onSuccess: async () => {
            getCheckoutSession?.();
            onSuccess?.(undefined, phoneNumber);
          },
          formikProps,
        });
    }
  };

  return (
    <Theme>
      <Formik<AuthValues>
        enableReinitialize
        initialValues={INITIAL_VALUES}
        onSubmit={handleSubmit}
        validationSchema={getValidationSchema({ flow, formAction, withTerms })}
      >
        {(formikProps) => {
          return (
            <Form className={`${cls.BASE} ${className}`} id={FORM_ID}>
              <AuthFormSections
                formAction={formAction}
                onFormToggleClick={handleFormToggleClick}
                onPhoneNumberChange={handlePhoneNumberChange}
                onSocialLoginSuccess={onSocialLoginSuccess}
                onSubmitStateChange={onSubmitStateChange}
                useFadeTransition
                showSocialLogin={showSocialLogin}
                submitButtonProps={submitButtonProps}
                withTerms={withTerms}
                {...formikProps}
              />
            </Form>
          );
        }}
      </Formik>
    </Theme>
  );
};

export default AuthForm;
